import { stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { IComponentStyle } from '@kurtosys/types/componentStyle/IComponentStyle';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles';
import { IConfiguration } from '../../../models/app/IConfiguration';
import { TStoreContext } from '../../../models/app/TStoreContext';
import { AppStore } from '../../App/stores/AppStore';
import { QuizStore } from '../../App/stores/QuizStore';
import { QuestionStore } from '../../Question/stores/QuestionStore';
import { QuestionWizardStore } from '../../QuestionWizard/stores/QuestionWizardStore';
import { IResultsButtons, IResultsConfiguration } from '../models';
const StoreBase = stores.base.StoreBase<IConfiguration, IComponentStyles>;
type TranslationStore = stores.TranslationStore<IConfiguration, IComponentStyles>;

type IRedirectButtonProps = components.base.RedirectButton.models.IRedirectButtonProps;
type ILinearProgressBarProps = components.base.LinearProgressBar.models.ILinearProgressBarProps;
type IIconProps = components.base.Icon.models.IIconProps;
type IFactProps = components.fact.Fact.models.IFactProps;
type ITextProps = components.base.Text.models.ITextProps;
type IButtonProps = components.base.Button.models.IButtonProps;

export class ResultsStore extends StoreBase {
	static componentKey = 'results' as const;

	@observable showReview = false;
	@observable calculating = false;

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);
		makeObservable(this);
	}

	@computed
	get appStore() {
		return this.storeContext.get<AppStore>('appStore');
	}

	@computed
	get quizStore() {
		return this.storeContext.get<QuizStore>('quizStore');
	}

	@computed
	get questionStore() {
		return this.storeContext.get<QuestionStore>('questionStore');
	}

	@computed
	get questionWizardStore() {
		return this.storeContext.get<QuestionWizardStore>('questionWizardStore');
	}

	@computed
	get translationStore() {
		return this.storeContext.get<TranslationStore>('translationStore');
	}

	@computed
	get configuration(): IResultsConfiguration | undefined {
		if (this.storeContext && this.appStore) {
			return this.appStore.getComponentConfiguration(ResultsStore.componentKey);
		}
	}

	@action
	async initialize(): Promise<void> {
		return;
	}

	@action startCalculating() {
		this.calculating = true;
	}

	finishCalculating() {
		setTimeout(() => {
			runInAction(() => {
				this.calculating = false;
			});
		}, this.minimumCalculatingTime);
	}

	@computed
	get minimumCalculatingTime(): number {
		return (this.configuration && this.configuration.minimumCalcutingTime) || 0;
	}

	@computed
	get calculatingMessage(): string {
		return (
			(this.configuration && this.configuration.calculatingMessage) ||
			'Quiz complete. Results are being recorded.'
		);
	}

	@computed
	get defaultButtons(): IResultsButtons {
		return {
			restart: {
				text: 'Restart Quiz',
			},
			review: {
				text: 'Review Answers',
			},
		};
	}

	@computed
	get buttons(): IResultsButtons | undefined {
		return this.configuration && this.configuration.buttons;
	}

	@computed
	get progressBarProps(): ILinearProgressBarProps {
		const defaultProps: ILinearProgressBarProps = {
			backgroundColor: '#eeeeee',
			activeColor: 'green',
		};
		return { ...defaultProps, ...(this.configuration && this.configuration.progressBar) };
	}

	@computed
	get answersFactProps(): IFactProps {
		const answersTile = this.configuration && this.configuration.answersTile;
		return {
			title: (answersTile && answersTile.title) || 'Answers',
			value: `${this.quizStore.score}/${this.quizStore.totalQuestions}`,
		};
	}

	@computed
	get percentFactProps(): IFactProps {
		const percentTile = this.configuration && this.configuration.percentTile;
		const formatter = (percentTile && percentTile.format) || {
			type: 'percent',
			options: {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			},
		};
		const value = common.commonUtils.formatter(
			formatter,
			this.quizStore.percentage,
			this.translationStore.translate,
		);
		return {
			title: (percentTile && percentTile.title) || 'Percent',
			value: `${value}`,
		};
	}

	@computed
	get restartButtonProps(): IButtonProps | undefined {
		const restartButton = (this.buttons && this.buttons.restart) || this.defaultButtons.restart;
		let disabled = false;
		if (restartButton && restartButton.disableOnComplete && this.quizStore.percentage === 1) {
			disabled = true;
		}

		if (this.quizStore.articleResults) {
			const { disableOnComplete, ...buttonProps } =
				(this.buttons && this.buttons.restart) || this.defaultButtons.restart || {};
			return {
				...buttonProps,
				onClick: () => {
					this.quizStore.restartQuiz();
				},
				disabled,
			};
		}
	}

	@computed
	get reviewButtonProps(): IButtonProps | undefined {
		if (this.quizStore.articleResults) {
			return {
				...((this.buttons && this.buttons.review) || this.defaultButtons.review),
				onClick: () => {
					runInAction(() => {
						this.showReview = !this.showReview;
					});
				},
			};
		}
	}

	@computed
	get continueButtonProps(): IRedirectButtonProps | undefined {
		const allCorrect =
			(this.quizStore.articleResults &&
				common.commonUtils.isNullOrEmpty(this.quizStore.articleResults.incorrectAnswers)) ||
			(this.quizStore.isComplete && !this.quizStore.articleResults);
		if (allCorrect) {
			return this.configuration && this.configuration.buttons && this.configuration.buttons.continue;
		}
	}

	@computed
	get reviewQuestions(): any[] {
		const incorrectAnswers =
			(this.quizStore.articleResults && this.quizStore.articleResults.incorrectAnswers) || [];
		return this.quizStore.questions.map((question) => {
			const { id, name, answers = [] } = question;
			const userAnswer = this.quizStore.userAnswers[id];
			const isCorrect = !incorrectAnswers.includes(id);
			return {
				name,
				radioButtonsProps: {
					value: userAnswer,
					hasHtmlContent: (this.configuration && this.configuration.allowHtmlContent) || false,
					options: answers.map((answer, index) => {
						return {
							value: index,
							label: answer.answer,
						};
					}),
					selectedOptionStyle: this.getSelectedOptionStyle(isCorrect),
					selectedOptionIcon: this.getSelectedOptionIcon(isCorrect),
				},
			};
		});
	}

	@computed
	get answerPromptProps(): ITextProps {
		return this.questionStore.answerPromptProps;
	}

	@computed
	get hideCommentaryOnComplete(): boolean {
		return (this.configuration && this.configuration.hideCommentaryOnComplete) || false;
	}

	getSelectedOptionStyle(isCorrect: boolean): IComponentStyle | undefined {
		if (isCorrect) {
			return this.configuration && this.configuration.correctAnswerStyle;
		}
		return this.configuration && this.configuration.incorrectAnswerStyle;
	}

	getSelectedOptionIcon(isCorrect: boolean): IIconProps | undefined {
		if (isCorrect) {
			return this.configuration && this.configuration.correctAnswerIcon;
		}
		return this.configuration && this.configuration.incorrectAnswerIcon;
	}
}
